'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { Button } from '@workspace/ui/components/button';
import { AddVideoIcon, Icon } from '@/src/components/ui';
import { Textarea } from '@workspace/ui/components/textarea';
import {
  extractYouTubeId,
  getYouTubeThumbnail,
  getYouTubeVideoInfoNoAPI,
  isValidYouTubeUrl,
  YouTubeVideoInfo,
} from '@/src/utils/youtube';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EmptyTileWrapper } from '../../profile/components';
import { useBoolean } from '@/src/hooks/use-boolean';

export type TrailerItem = {
  clientId?: string;
  url: string;
  title: string;
  thumbnail?: string;
  order?: number;
};

type TrailersProps = {
  value?: TrailerItem[];
  trailers?: string[];
  onChange?: (next: TrailerItem[]) => void;
  className?: string;
  readonly?: boolean;
};

const useMounted = () => {
  const [m, setM] = useState(false);
  useEffect(() => setM(true), []);
  return m;
};

const reorderWithOrder = (arr: TrailerItem[]) =>
  arr.map((t, idx) => ({ ...t, order: idx }));

function SortableThumb({
  item,
  onRemove,
}: {
  item: TrailerItem;
  onRemove: (clientId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.clientId as string });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 2 : 1,
    touchAction: 'none',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group relative rounded-lg border bg-red-youtube border-gray-200 p-5 flex gap-2',
        'w-[280px] h-[140px] md:w-[442px] md:h-[214px] shrink-0'
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className={cn(
          'absolute right-1/2 -bottom-3 translate-x-1/2 rounded-full bg-black/90 text-white p-2 shadow',
          'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
          'transition-opacity duration-150'
        )}
        aria-label="Drag to reorder"
        title="Drag to reorder"
      >
        <Icon name="grip" className="size-4" />
      </button>

      <button
        type="button"
        onClick={() => onRemove(item.clientId as string)}
        className={cn(
          'absolute -top-3 -right-3 rounded-full bg-black/90 text-white p-2 shadow',
          'opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto',
          'transition-opacity duration-150'
        )}
        aria-label="Remove"
        title="Remove"
      >
        <Icon name="delete-tile" className="size-4" />
      </button>

      <Icon
        name="video-icon"
        className={cn('absolute top-3 left-4 size-11 text-white')}
      />

      <div className="flex flex-col justify-end">
        <p className="text-xs md:text-base font-medium hyphens-auto leading-tight line-clamp-3 hover:underline cursor-default">
          {item.title || 'YouTube Video'}
        </p>
      </div>

      <div className="flex flex-col gap-5 ml-auto">
        <img
          src={item.thumbnail ?? getYouTubeThumbnail(item.url) ?? ''}
          alt="Video Thumbnail"
          className="w-[201px] h-[118px] rounded-md object-cover"
        />
        <Button
          variant="outline"
          className="text-xs md:text-sm bg-white"
          onMouseDown={(e: React.MouseEvent) => {
            e.stopPropagation();
            item.url && window.open(item.url, '_blank');
          }}
        >
          <Icon name="video-icon" className="size-4" />
          Watch on YouTube
        </Button>
      </div>
    </div>
  );
}

function Thumb({ url }: { url: string }) {
  const id = React.useMemo(() => extractYouTubeId(url), [url]);

  const [title, setTitle] = React.useState<string>('YouTube Video');
  const [thumb, setThumb] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState<boolean>(true);

  React.useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const fallbackThumb =
          getYouTubeThumbnail(url) ??
          (id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null);

        if (!id) {
          if (!cancelled) {
            setTitle('YouTube Video');
            setThumb(fallbackThumb);
            setLoading(false);
          }
          return;
        }

        const info = await getYouTubeVideoInfoNoAPI(id).catch(() => null);

        if (!cancelled) {
          setTitle(info?.title || 'YouTube Video');
          setThumb(fallbackThumb);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setTitle('YouTube Video');
          setThumb(id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null);
          setLoading(false);
        }
      }
    }

    setLoading(true);
    load();

    return () => {
      cancelled = true;
    };
  }, [id, url]);

  return (
    <div
      className={cn(
        'group relative rounded-lg border bg-red-youtube border-gray-200 md:p-5 p-2 flex gap-2',
        'w-[280px] h-[140px] md:w-[442px] md:h-[214px] shrink-0'
      )}
    >
      <Icon
        name="video-icon"
        className={cn('absolute top-3 left-4 size-11 text-white')}
      />

      <div className="flex flex-col justify-end">
        <p className="text-xs md:text-base font-medium hyphens-auto leading-tight line-clamp-3 hover:underline cursor-default">
          {loading ? 'Loading…' : title}
        </p>
      </div>

      <div className="flex flex-col md:gap-5 gap-1 ml-auto">
        {loading ? (
          <div className="w-[201px] h-[118px] rounded-md bg-black/20 animate-pulse" />
        ) : (
          <img
            src={thumb || ''}
            alt="Video Thumbnail"
            className="w-[201px] h-[118px] rounded-md object-cover"
          />
        )}

        <Button
          asChild
          variant="outline"
          className="text-xs md:text-sm bg-white"
        >
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Icon name="video-icon" className="size-4" />
            Watch on YouTube
          </a>
        </Button>
      </div>
    </div>
  );
}

function AddInlineTile({
  onSubmit,
  onCancel,
}: {
  onSubmit: (url: string) => void;
  onCancel: () => void;
}) {
  const [tempValue, setTempValue] = useState('');
  const [videoError, setVideoError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const handleSave = () => {
    const trimmed = tempValue.trim();
    if (!trimmed) {
      onCancel();
      return;
    }
    if (!isValidYouTubeUrl(trimmed)) {
      setVideoError('Please attach YouTube video link');
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <div className="md:w-[214px] md:h-[214px] w-[214px] h-[214px] shrink-0">
      <EmptyTileWrapper isMyProfile disableHover className="rounded-2xl">
        <div className="flex flex-col justify-between w-full h-full transition-colors cursor-pointer md:p-5 p-4 text-black bg-red-youtube-active rounded-2xl">
          <Icon name="video-icon" className="size-8 md:size-11" />
          <div className="flex flex-col space-y-2">
            <Textarea
              ref={inputRef}
              value={tempValue}
              onChange={(e) => setTempValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSave();
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancel();
                }
              }}
              onBlur={handleSave}
              placeholder="Paste YouTube link here (e.g., https://www.youtube.com/watch?v=...)"
              className="w-full p-0 text-xs md:text-sm border-none focus:outline-none h-fit font-light md:placeholder:text-sm placeholder:text-xs bg-transparent"
              style={{
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                resize: 'none',
              }}
            />
            {videoError && (
              <span className="text-red-600 text-xs flex items-center">
                {videoError}
              </span>
            )}
          </div>
        </div>
      </EmptyTileWrapper>
    </div>
  );
}

export function Trailers({
  value,
  onChange,
  className,
  trailers,
  readonly = false,
}: TrailersProps) {
  const mounted = useMounted();
  const isAdding = useBoolean(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanLeft(scrollLeft > 0);
    setCanRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => updateArrows();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateArrows);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateArrows);
    };
  }, [mounted, value]);

  const scrollByStep = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(300, Math.floor(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === 'left' ? -step : step, behavior: 'smooth' });
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const items = useMemo(
    () => [...(value ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [value]
  );

  const handleAddConfirmed = async (url: string) => {
    isAdding.onFalse();
    const id = extractYouTubeId(url);
    if (!id) return;

    let info: Partial<YouTubeVideoInfo> | null = null;
    try {
      info = await getYouTubeVideoInfoNoAPI(id);
    } catch (_) {}

    const thumb =
      getYouTubeThumbnail(url) ?? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

    const next: TrailerItem = {
      clientId:
        globalThis.crypto?.randomUUID?.() ??
        `yt_${Date.now()}_${Math.random().toString(36).slice(2)}`,
      url,
      title: info?.title || '',
      thumbnail: thumb,
      order: items.length,
    };

    onChange?.(reorderWithOrder([...items, next]));
    requestAnimationFrame(() => scrollByStep('right'));
  };

  const handleRemove = (id: string) => {
    const next = items.filter((t) => t.clientId !== id);
    onChange?.(reorderWithOrder(next));
  };

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.clientId === active.id);
    const newIndex = items.findIndex((i) => i.clientId === over.id);
    const moved = arrayMove(items, oldIndex, newIndex);
    onChange?.(reorderWithOrder(moved));
  };

  if (
    (!value || value.length === 0) &&
    (!trailers || trailers.length === 0) &&
    readonly
  ) {
    return null;
  }

  return (
    <div className={cn('w-full', className)}>
      <div className="flex items-center justify-between mb-2">
        <h3 className="md:text-2xl! text-lg! font-semibold! md:mt-9 mt-5">
          {(value && value.length > 1) || (trailers && trailers.length > 1)
            ? 'Trailers'
            : 'Trailer'}
        </h3>

        {trailers && trailers.length > 2 && readonly && (
          <div className="flex items-center gap-2 md:mt-9">
            <button
              type="button"
              onClick={() => scrollByStep('left')}
              disabled={!canLeft}
              className={cn(
                'inline-flex items-center justify-center size-9 rounded-full bg-white shadow border',
                'hover:bg-blue-primary active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              aria-label="Scroll left"
              title="Scroll left"
            >
              <Icon name="left" className="size-3" />
            </button>
            <button
              type="button"
              onClick={() => scrollByStep('right')}
              disabled={!canRight}
              className={cn(
                'inline-flex items-center justify-center size-9 rounded-full bg-white shadow border',
                'hover:bg-blue-primary active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed'
              )}
              aria-label="Scroll right"
              title="Scroll right"
            >
              <Icon name="right" className="size-3" />
            </button>
          </div>
        )}
      </div>

      <div className="relative">
        {mounted ? (
          trailers && readonly ? (
            <div
              ref={scrollRef}
              className="md:h-[250px] flex gap-3 items-center overflow-x-auto hide-scrollbar"
            >
              {trailers.map((tr, i) => (
                <Thumb key={tr + i} url={tr} />
              ))}

              {!readonly || !trailers ? (
                isAdding.value ? (
                  <AddInlineTile
                    onSubmit={handleAddConfirmed}
                    onCancel={() => isAdding.onFalse()}
                  />
                ) : (
                  <div className="md:w-[214px] md:h-[214px] w-[214px] h-[214px]">
                    <EmptyTileWrapper
                      isMyProfile
                      onClick={() => isAdding.onTrue()}
                    >
                      <div className="flex flex-col items-center h-full w-full justify-center gap-3 min-w-[214px]">
                        <AddVideoIcon />
                        <span className="text-sm font-medium text-black/40">
                          Add video
                        </span>
                      </div>
                    </EmptyTileWrapper>
                  </div>
                )
              ) : null}
            </div>
          ) : (
            <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
              <SortableContext
                items={items.map((i) => i.clientId as string)}
                strategy={horizontalListSortingStrategy}
              >
                <div
                  ref={scrollRef}
                  className="h-[250px] flex gap-3 items-center overflow-x-auto hide-scrollbar"
                >
                  {items.map((tr) => (
                    <SortableThumb
                      key={tr.clientId}
                      item={tr}
                      onRemove={handleRemove}
                    />
                  ))}

                  {!readonly || !trailers ? (
                    isAdding.value ? (
                      <AddInlineTile
                        onSubmit={handleAddConfirmed}
                        onCancel={() => isAdding.onFalse()}
                      />
                    ) : (
                      <div className="md:w-[214px] md:h-[214px] w-[214px] h-[214px]">
                        <EmptyTileWrapper
                          isMyProfile
                          onClick={() => isAdding.onTrue()}
                        >
                          <div className="flex flex-col items-center h-full w-full justify-center gap-3 min-w-[214px]">
                            <AddVideoIcon />
                            <span className="text-sm font-medium text-black/40">
                              Add video
                            </span>
                          </div>
                        </EmptyTileWrapper>
                      </div>
                    )
                  ) : null}
                </div>
              </SortableContext>
            </DndContext>
          )
        ) : (
          <div className="flex gap-2 overflow-x-auto py-5 hide-scrollbar">
            {items.map((tr) => (
              <div
                key={tr.clientId}
                className="w-[280px] h-[140px] md:w-[442px] md:h-[214px] rounded-lg bg-gray-100 animate-pulse"
              />
            ))}
            <div className="w-[214px] h-[214px] rounded-lg border-gray-300" />
          </div>
        )}
      </div>
    </div>
  );
}
